// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import redirectToSignInSessionTimeout from "@ces/App/Handlers/XNG/RedirectToSignInSessionTimeout";
import { markSkipOnCompleteIfOnSuccessIsSet, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { updateCart as updateCartApi } from "@insite/client-framework/Services/CartService";
import { createShipTo, updateShipTo } from "@insite/client-framework/Services/CustomersService";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";

import {
    AddOrUpdateShipTo,
    ExecuteOnSuccessCallback,
    UpdateCart as UpdateCartHandler,
    chain as UpdateCartHandlerChain,
} from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import translate from "@insite/client-framework/Translate";
import { BaseAddressModel } from "@insite/client-framework/Types/ApiModels";

export const XngUpdateCartHandler: typeof UpdateCartHandler = async props => {
    const cart = props.apiParameter.cart;
    // Add try catch
    try {
        props.apiResult = await updateCartApi({
            cart: {
                ...cart,
            },
        });
    } catch (error) {
        props.dispatch({
            type: "Pages/CheckoutShipping/CompleteUpdateCart",
        });
        // XNG-Change
        (props.parameter as any).onError?.(
            error.status === 400 ? error.errorJson.message : translate("There is an error please contact support team"),
        );

        props.dispatch(redirectToSignInSessionTimeout({ errorMessage: error.errorJson.message }));
    }
};

export const XngExecuteOnSuccessCallback: any = (props: any) => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.(props.apiResult);
};

export const xAreAddressesTheSame = (address1: BaseAddressModel, address2: BaseAddressModel) => {
    // XNG-Change add properties.
    return (
        address1?.firstName === address2?.firstName &&
        address1?.lastName === address2?.lastName &&
        address1?.attention === address2?.attention &&
        address1?.companyName === address2?.companyName &&
        address1?.address1 === address2?.address1 &&
        address1?.address2 === address2?.address2 &&
        address1?.address4 === address2?.address4 &&
        address1?.address3 === address2?.address3 &&
        address1?.country?.id === address2?.country?.id &&
        address1?.city === address2?.city &&
        address1?.state?.id === address2?.state?.id &&
        address1?.postalCode === address2?.postalCode &&
        address1?.phone === address2?.phone &&
        address1?.email === address2?.email &&
        address1?.fax === address2?.fax &&
        address1?.properties?.isSignatureRequired === address2?.properties?.isSignatureRequired &&
        address1?.properties?.hasLockBox === address2?.properties?.hasLockBox &&
        address1?.properties?.siteName === address2?.properties?.siteName
    );
};

export const XngCartAddOrUpdateShipTo: typeof AddOrUpdateShipTo = async props => {
    const { dispatch, getState, cart } = props;
    const state = getState();
    const {
        pages: {
            checkoutShipping: { shippingAddressFormState },
        },
    } = state;
    if (!shippingAddressFormState) {
        return;
    }

    const shipTo = getShipToState(state, cart.shipToId).value;
    const billTo = getBillToState(state, cart.billToId).value;
    if (!shipTo || !billTo) {
        throw new Error("There was no shipTo or billTo loaded for the current cart");
    }

    const { address: localModifiedShipTo } = shippingAddressFormState;
    if (!xAreAddressesTheSame(shipTo, localModifiedShipTo)) {
        try {
            props.updatedShipTo = localModifiedShipTo.isNew
                ? await createShipTo({ shipTo: localModifiedShipTo })
                : await updateShipTo({ shipTo: localModifiedShipTo, billToId: billTo.id });

            dispatch(loadShipTo({ shipToId: props.updatedShipTo.id, billToId: billTo.id }));

            props.apiParameter.cart.shipToId = props.updatedShipTo.id;
        } catch (error) {
            props.dispatch({
                type: "Pages/CheckoutShipping/CompleteUpdateCart",
            });
            // XNG-Change
            (props.parameter as any).onError?.(
                error.status === 400
                    ? error.errorJson.message
                    : translate("There is an error please contact support team"),
            );
            return false;
        }
    }
};

replaceInChain(UpdateCartHandlerChain, UpdateCartHandler, XngUpdateCartHandler);
replaceInChain(UpdateCartHandlerChain, ExecuteOnSuccessCallback, XngExecuteOnSuccessCallback);
replaceInChain(UpdateCartHandlerChain, AddOrUpdateShipTo, XngCartAddOrUpdateShipTo);
